import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';

interface Tag {
  id: string;
  name: string;
  color?: string;
  user_id: string;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

interface CreateTagData {
  name: string;
  color?: string;
}

interface UpdateTagData {
  name?: string;
  color?: string;
}

export const useTags = (category?: string) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = category ? `/api/tags?category=${category}` : '/api/tags';
      const result = await apiClient.request<{ tags: Tag[] }>(url);

      if (result.error) {
        setError(result.error.message);
      } else if (result.data) {
        setTags(result.data.tags);
      }
    } catch (err) {
      setError('Failed to fetch tags');
      console.error('Fetch tags error:', err);
    } finally {
      setLoading(false);
    }
  }, [category]);

  // Create tag
  const createTag = useCallback(async (data: CreateTagData) => {
    setError(null);

    try {
      const result = await apiClient.request<{ tag: Tag }>(
        '/api/tags',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );

      if (result.error) {
        setError(result.error.message);
        return null;
      }

      if (result.data) {
        // Add new tag to the list
        setTags(prev => [...prev, result.data!.tag]);
        return result.data.tag;
      }

      return null;
    } catch (err) {
      setError('Failed to create tag');
      console.error('Create tag error:', err);
      return null;
    }
  }, []);

  // Update tag
  const updateTag = useCallback(async (id: string, data: UpdateTagData) => {
    setError(null);

    try {
      const result = await apiClient.request<{ tag: Tag }>(
        `/api/tags/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      );

      if (result.error) {
        setError(result.error.message);
        return null;
      }

      if (result.data) {
        // Update tag in the list
        setTags(prev =>
          prev.map(tag => (tag.id === id ? result.data!.tag : tag))
        );
        return result.data.tag;
      }

      return null;
    } catch (err) {
      setError('Failed to update tag');
      console.error('Update tag error:', err);
      return null;
    }
  }, []);

  // Delete tag
  const deleteTag = useCallback(async (id: string) => {
    setError(null);

    try {
      const result = await apiClient.request<{ message: string }>(
        `/api/tags/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (result.error) {
        setError(result.error.message);
        return false;
      }

      // Remove tag from the list
      setTags(prev => prev.filter(tag => tag.id !== id));
      return true;
    } catch (err) {
      setError('Failed to delete tag');
      console.error('Delete tag error:', err);
      return false;
    }
  }, []);

  // Get popular tags
  const getPopularTags = useCallback(async (limit: number = 10) => {
    try {
      const result = await apiClient.request<{ tags: Tag[] }>(
        `/api/tags/popular?limit=${limit}`
      );

      if (result.error) {
        console.error('Get popular tags error:', result.error);
        return [];
      }

      return result.data?.tags || [];
    } catch (err) {
      console.error('Get popular tags error:', err);
      return [];
    }
  }, []);

  // Refresh tags (alias for fetchTags)
  const refresh = fetchTags;

  // Load tags on mount
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return {
    tags,
    loading,
    error,
    createTag,
    updateTag,
    deleteTag,
    getPopularTags,
    refresh,
  };
};
